import { useState, useEffect, useCallback } from "react";
import { servicesService } from "../../../shared/services/apis";
import type {
  Service,
  ServiceQueryParams,
  CreateServiceData,
  UpdateServiceData,
} from "../../../shared/types";

interface UseServicesManagementReturn {
  services: Service[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  fetchServices: (params?: ServiceQueryParams) => Promise<void>;
  createService: (data: CreateServiceData) => Promise<void>;
  updateService: (id: string, data: UpdateServiceData) => Promise<void>;
  toggleServiceStatus: (id: string) => Promise<void>;
  deleteService: (id: string) => Promise<void>;
  refreshServices: () => Promise<void>;
}

export const useServicesManagement = (): UseServicesManagementReturn => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchServices = useCallback(async (params?: ServiceQueryParams) => {
    try {
      setLoading(true);
      setError(null);
      const data = await servicesService.getServices(params);
      setServices(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch services";
      setError(errorMessage);
      console.error("Error fetching services:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createService = useCallback(async (data: CreateServiceData) => {
    try {
      setCreating(true);
      setError(null);
      const newService = await servicesService.createService(data);
      setServices(prev => [newService, ...prev]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create service";
      setError(errorMessage);
      console.error("Error creating service:", err);
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  const updateService = useCallback(async (id: string, data: UpdateServiceData) => {
    try {
      setUpdating(true);
      setError(null);
      const updatedService = await servicesService.updateService(id, data);
      setServices(prev =>
        prev.map(service => (service.id === id ? updatedService : service))
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update service";
      setError(errorMessage);
      console.error("Error updating service:", err);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const toggleServiceStatus = useCallback(async (id: string) => {
    try {
      setUpdating(true);
      setError(null);
      const updatedService = await servicesService.toggleServiceStatus(id);
      setServices(prev =>
        prev.map(service => (service.id === id ? updatedService : service))
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to toggle service status";
      setError(errorMessage);
      console.error("Error toggling service status:", err);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const deleteService = useCallback(async (id: string) => {
    try {
      setDeleting(true);
      setError(null);
      await servicesService.deleteService(id);
      setServices(prev => prev.filter(service => service.id !== id));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete service";
      setError(errorMessage);
      console.error("Error deleting service:", err);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  const refreshServices = useCallback(() => {
    return fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  return {
    services,
    loading,
    error,
    creating,
    updating,
    deleting,
    fetchServices,
    createService,
    updateService,
    toggleServiceStatus,
    deleteService,
    refreshServices,
  };
};